import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
  TextField,
} from "@mui/material";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  addCollectionEntries,
  getCollectionById,
  getCollectionEntries,
  getCollectionResolvedEntities,
  getCollectionSelectableItems,
  getCollectionSelectableStores,
  reorderCollectionEntries,
  removeCollectionEntry,
} from "../../../../service/BuyerConfiguration/CollectionManagement/collectionService";
import { notifyError, notifySuccess } from "../../../../components/ToastContainer/Toast/Toast";

type Props = {
  collectionId: number;
  onClose: () => void;
};

type SelectorType = "item" | "store";

const ManageCollectionEntries: React.FC<Props> = ({ collectionId, onClose }) => {
  const [entries, setEntries] = useState<any[]>([]);
  const [selectorType, setSelectorType] = useState<SelectorType>("item");
  const [storeOptions, setStoreOptions] = useState<Array<{ id: number; label: string }>>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);
  const [options, setOptions] = useState<Array<{ id: number; label: string }>>([]);
  const [resolvedEntities, setResolvedEntities] = useState<any[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<Array<{ id: number; label: string }>>(
    [],
  );
  const [loading, setLoading] = useState(false);

  const selectedIds = useMemo(() => selectedOptions.map((option) => option.id), [selectedOptions]);

  const loadEntries = async () => {
    setLoading(true);
    try {
      const res: any = await getCollectionEntries(collectionId);
      const rows = res?.data ?? res ?? [];
      setEntries(Array.isArray(rows) ? rows : []);
    } catch (error: any) {
      notifyError(error?.response?.data?.message || "Failed to fetch entries");
    } finally {
      setLoading(false);
    }
  };

  const loadResolvedEntities = async () => {
    try {
      const res: any = await getCollectionResolvedEntities(collectionId, {
        page: 1,
        limit: 100,
      });
      const payload = res?.data ?? res;
      setResolvedEntities(Array.isArray(payload?.data) ? payload.data : []);
    } catch {
      setResolvedEntities([]);
    }
  };

  const loadCollectionType = async () => {
    try {
      const res: any = await getCollectionById(collectionId);
      const row = res?.data ?? res;
      if (row?.type === "store" || row?.type === "item") {
        setSelectorType(row.type);
      }
    } catch {
      // no-op, keep default
    }
  };

  const loadSelectableOptions = async (targetType: SelectorType) => {
    setLoading(true);
    try {
      if (targetType === "store") {
        const response: any = await getCollectionSelectableStores({
          page: 1,
          limit: 100,
        });
        const stores = response?.data?.data || [];
        setOptions(
          (Array.isArray(stores) ? stores : []).map((store: any) => ({
            id: Number(store.id),
            label: store.name || `Store #${store.id}`,
          })),
        );
      } else {
        if (!selectedStoreId || selectedStoreId <= 0) {
          setOptions([]);
          return;
        }
        const response: any = await getCollectionSelectableItems({
          page: 1,
          limit: 100,
          store_id: selectedStoreId,
        });
        const products = response?.data?.data || [];
        setOptions(
          (Array.isArray(products) ? products : []).map((item: any) => ({
            id: Number(item.id),
            label: `${item.name || `Item #${item.id}`} by ${
              item?.store?.name ||
              item?.store_name ||
              storeOptions.find((store) => store.id === selectedStoreId)?.label ||
              "Store"
            }`,
          })),
        );
      }
    } catch (error: any) {
      setOptions([]);
      notifyError(error?.response?.data?.message || "Failed to fetch selectable list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCollectionType();
    loadEntries();
    loadResolvedEntities();
  }, [collectionId]);

  useEffect(() => {
    getCollectionSelectableStores({ page: 1, limit: 100 })
      .then((response: any) => {
        const stores = response?.data?.data || [];
        setStoreOptions(
          (Array.isArray(stores) ? stores : []).map((store: any) => ({
            id: Number(store.id),
            label: store.name || `Store #${store.id}`,
          })),
        );
      })
      .catch(() => {
        setStoreOptions([]);
      });
  }, []);

  useEffect(() => {
    setSelectedOptions([]);
    loadSelectableOptions(selectorType);
  }, [selectorType, selectedStoreId]);

  const handleAddEntries = async () => {
    if (selectedIds.length === 0) return;
    setLoading(true);
    try {
      await addCollectionEntries(collectionId, selectedIds);
      notifySuccess("Entries added successfully");
      setSelectedOptions([]);
      await loadEntries();
      await loadResolvedEntities();
    } catch (error: any) {
      notifyError(error?.response?.data?.message || "Failed to add entries");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveEntry = async (entryId: number) => {
    setLoading(true);
    try {
      await removeCollectionEntry(collectionId, entryId);
      notifySuccess("Entry removed");
      await loadEntries();
      await loadResolvedEntities();
    } catch (error: any) {
      notifyError(error?.response?.data?.message || "Failed to remove entry");
    } finally {
      setLoading(false);
    }
  };

  const handleEntryOrderChange = async (params: any) => {
    const movedEntryId = Number(params?.row?.id);
    const targetIndex = Number(params?.targetIndex ?? 0);
    if (!movedEntryId || Number.isNaN(targetIndex)) return;
    const sorted = [...entries].sort(
      (a, b) => Number(a.sequence || 0) - Number(b.sequence || 0),
    );
    const currentIndex = sorted.findIndex((row) => Number(row.id) === movedEntryId);
    if (currentIndex < 0) return;
    const [moved] = sorted.splice(currentIndex, 1);
    sorted.splice(targetIndex, 0, moved);

    const reorderPayload = sorted.map((entry, index) => ({
      entry_id: Number(entry.id),
      new_position: index + 1,
    }));
    try {
      await reorderCollectionEntries(collectionId, reorderPayload);
      notifySuccess("Entry sequence updated");
      await loadEntries();
      await loadResolvedEntities();
    } catch (error: any) {
      notifyError(error?.response?.data?.message || "Failed to reorder entries");
    }
  };

  const entryRows = [...entries].sort(
    (a, b) => Number(a.sequence || 0) - Number(b.sequence || 0),
  );
  const entryColumns: GridColDef[] = [
    {
      field: "sequence",
      headerName: "Sequence",
      flex: 0.7,
      minWidth: 100,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "title",
      headerName: selectorType === "item" ? "Item" : "Store",
      flex: 1.8,
      minWidth: 220,
      renderCell: (params: any) => {
        const resolved = resolvedEntities.find(
          (entity) => Number(entity.id) === Number(params.row.entity_id),
        );
        const title = (
          resolved?.name ||
          resolved?.item_name ||
          resolved?.store_name ||
          `ID ${params.row.entity_id}`
        );
        return <span>{title}</span>;
      },
    },
    {
      field: "entity_id",
      headerName: "Entity ID",
      flex: 0.8,
      minWidth: 110,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.7,
      minWidth: 100,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <IconButton
          size="small"
          color="error"
          onClick={() => handleRemoveEntry(Number(params.row.id))}
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.25}
        sx={{ mb: 2, alignItems: "stretch" }}
      >
        <FormControl sx={{ minWidth: 160 }}>
          <Select
            value={selectorType}
            size="small"
            disabled
            displayEmpty
            renderValue={(selected) => (selected ? String(selected) : "Type")}
            onChange={(e) => setSelectorType(e.target.value as SelectorType)}
            sx={{ minHeight: 40 }}
          >
            <MenuItem value="item">Item</MenuItem>
            <MenuItem value="store">Store</MenuItem>
          </Select>
        </FormControl>
        {selectorType === "item" && (
          <FormControl sx={{ minWidth: 220 }}>
            <Select
              value={selectedStoreId ?? ""}
              size="small"
              displayEmpty
              renderValue={(selected) => {
                if (!selected) return "Store";
                const selectedStore = storeOptions.find(
                  (store) => Number(store.id) === Number(selected),
                );
                return selectedStore?.label || "Store";
              }}
              onChange={(e) => setSelectedStoreId(Number(e.target.value))}
              sx={{ minHeight: 40 }}
            >
              {storeOptions.map((store) => (
                <MenuItem key={store.id} value={store.id}>
                  {store.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Autocomplete
          multiple
          fullWidth
          size="small"
          limitTags={1}
          options={options}
          value={selectedOptions}
          onChange={(_, value) => setSelectedOptions(value)}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          loading={loading}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.id}
                size="small"
                label={option.label}
                sx={{ maxWidth: 220 }}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder={selectorType === "item" ? "Search items" : "Search stores"}
            />
          )}
        />
        <Button
          variant="contained"
          size="small"
          onClick={handleAddEntries}
          disabled={loading || selectedIds.length === 0 || (selectorType === "item" && !selectedStoreId)}
          sx={{ minWidth: 88, height: 40, alignSelf: { xs: "stretch", md: "center" } }}
        >
          Add
        </Button>
      </Stack>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {selectorType === "item" ? "Items List" : "Stores List"} ({resolvedEntities.length})
      </Typography>
      <DataGridPro
        autoHeight
        disableColumnMenu
        rows={entryRows}
        columns={entryColumns}
        getRowId={(row) => row.id}
        rowReordering
        onRowOrderChange={handleEntryOrderChange}
        sx={{
          border: 0,
          "& .MuiDataGrid-columnHeaders": { backgroundColor: "background.default" },
          "& .MuiDataGrid-cell": { alignItems: "center", display: "flex" },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
            outline: "none",
          },
          "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
            {
              outline: "none",
            },
          "& .MuiDataGrid-rowReorderCell": { cursor: "grab" },
          "& .MuiDataGrid-dragIcon": { display: "none !important" },
        }}
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </Box>
    </Box>
  );
};

export default ManageCollectionEntries;
